"use client";

import { Button } from "@/components/ui/button";
import { Phone, Lock } from "lucide-react";
import { useScrollReveal } from "@/hooks/common/useScrollReveal";
import { cornerShield } from "@/lib/gcsImagePaths";
import { useRouter } from "next/navigation";
import { RINGBA_DNI_PHONE, formatPhoneNumber } from "@/lib/constants";
import { navigateWithQueryParams } from "@/utils/navigationUtils";
import GcsImage from "./GcsImage";
import { cornerShieldDim } from "@/lib/communityData";

interface FinalCTASectionProps {
  title?: string;
  subtitle?: string;
  details?: string;
}
export default function FinalCTASection({
  title = "We do the work. You get the savings.",
  subtitle = "Answer 5 easy questions and we'll show you how much you could save.",
  details,
}: FinalCTASectionProps) {
  const router = useRouter();
  const { ref, isVisible } = useScrollReveal();
  const { ref: ctaShieldRef, isVisible: ctaShieldIsVisible } =
    useScrollReveal();

  return (
    <section
      ref={ref}
      className={`relative py-12 md:py-16 bg-primary text-primary-foreground overflow-hidden transition-opacity duration-700  ${
        isVisible ? "opacity-100" : "opacity-0"
      }`}
      style={{
        visibility: isVisible ? "visible" : "hidden",
      }}
    >
      <div className="container mx-auto px-4 text-center relative z-10">
        <h2 className="text-2xl md:text-3xl font-bold mb-6">{title}</h2>
        <p className="text-lg md:text-xl mb-8 opacity-95 max-w-2xl md:max-w-md mx-auto">
          {subtitle}
        </p>
        <div className="flex justify-center max-w-lg mx-auto">
          <Button
            variant={"compare"}
            onClick={() => navigateWithQueryParams(router, "/compare")}
          >
            COMPARE QUOTES
          </Button>
        </div>
        {details && <p className="text-xs mt-3 opacity-70 italic">{details}</p>}
        <div className="flex flex-wrap justify-center gap-6 mt-12 text-sm opacity-90">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            <a
              href={`tel:${RINGBA_DNI_PHONE}`}
              className="hover:underline"
              suppressHydrationWarning={true} // Ringba's Number Insertion changes the phone number client-side
            >
              {formatPhoneNumber(RINGBA_DNI_PHONE)}
            </a>
          </div>
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            <span>100% Secure & Confidential</span>
          </div>
        </div>
      </div>
      {/* Shield image - positioned absolutely, animated with transform to avoid CLS */}
      <div
        ref={ctaShieldRef}
        className="absolute bottom-0 right-0 pointer-events-none"
        style={{
          transform: ctaShieldIsVisible ? "translateX(0)" : "translateX(128px)",
          transition: "transform 1000ms ease-out",
          willChange: "transform",
        }}
      >
        <GcsImage
          path={cornerShield}
          width={cornerShieldDim.width}
          height={cornerShieldDim.height}
          alt="Decorative shield"
          className="h-56 md:h-73.5 w-auto opacity-60 brightness-0 invert"
        />
      </div>
    </section>
  );
}
